import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { IMG } from "@/lib/images";
import { SectionHeader, ComplianceNote } from "@/components/kit";
import { Gift, Heart, Share2, PackageCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Premium Spirits", "Wine Bundles", "Champagne", "Bar Carts", "Decanters", "Cocktail Glasses", "Shaker Sets", "Ice Molds", "Themed Recipe Kits"];

export default function Registry() {
  const [regs, setRegs] = useState([]);
  const [occasions, setOccasions] = useState([]);
  useEffect(() => { api.get("/registries").then((r) => setRegs(r.data)); api.get("/registry-occasions").then((r) => setOccasions(r.data)); }, []);
  const occImg = { Weddings: IMG.wedding, Housewarming: IMG.tropical, Christmas: IMG.champagne, "Fathers Day": IMG.oldfashioned, Birthdays: IMG.margarita, Anniversary: IMG.toast, "Mothers Day": IMG.spritz, "Holiday Party": IMG.champagne };

  return (
    <div>
      <section className="relative overflow-hidden">
        <img src={IMG.wedding} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        <div className="relative mx-auto max-w-6xl px-6 py-20">
          <p className="overline text-primary">The most original gift registry</p>
          <h1 className="mt-3 max-w-xl font-display text-5xl leading-tight lg:text-6xl">Gift the bar, not just the bottle.</h1>
          <p className="mt-4 max-w-lg text-muted-foreground">Create and share occasion-based alcohol and barware registries. Guests reserve and send exactly what you love.</p>
          <button data-testid="create-registry-btn" onClick={() => toast.success("Registry started", { description: "Pick an occasion to begin." })} className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground"><Gift size={18} /> Create a Registry</button>
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-16 px-6 py-14">
        <section>
          <SectionHeader overline="Every celebration" title="Registry occasions" />
          <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
            {occasions.map((o) => (
              <button key={o} onClick={() => toast.success(`Starting a ${o} registry`)} className="card-lift group relative aspect-[4/3] overflow-hidden rounded-xl">
                <img src={occImg[o] || IMG.champagne} alt={o} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <span className="absolute bottom-3 left-3 font-display text-xl text-white">{o}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="grid gap-6 sm:grid-cols-3">
          {[{ i: Gift, t: "Pick your items", d: "Add bottles, barware and recipe kits to your registry." }, { i: Share2, t: "Share with guests", d: "Send a beautiful link for any occasion." }, { i: PackageCheck, t: "They gift, you host", d: "Guests reserve & purchase through our partners." }].map((s) => (
            <div key={s.t} className="rounded-xl border border-border bg-card p-6"><s.i className="text-primary" /><h3 className="mt-3 font-display text-2xl">{s.t}</h3><p className="mt-1 text-sm text-muted-foreground">{s.d}</p></div>
          ))}
        </section>

        <section>
          <SectionHeader overline="Featured registries" title="See how it works" />
          <div className="grid gap-6 md:grid-cols-2">
            {regs.map((r) => (
              <Link key={r.id} to={`/registry/${r.id}`} data-testid={`registry-card-${r.id}`} className="card-lift group flex gap-4 overflow-hidden rounded-xl border border-border bg-card p-4">
                <img src={r.image} alt="" className="h-28 w-28 shrink-0 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <span className="overline text-primary">{r.occasion}</span>
                  <h3 className="font-display text-2xl leading-tight hover-underline">{r.title}</h3>
                  <p className="text-sm text-muted-foreground">{r.host} · {r.theme}</p>
                  <div className="mt-3"><div className="h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${r.progress}%` }} /></div><p className="mt-1 text-xs text-muted-foreground">{r.received} of {r.total} gifted · {r.progress}%</p></div>
                </div>
                <ArrowRight className="self-center text-muted-foreground group-hover:text-primary" size={18} />
              </Link>
            ))}
          </div>
        </section>

        <section>
          <SectionHeader overline="Popular gift categories" title="What to register for" />
          <div className="flex flex-wrap gap-2">{CATEGORIES.map((c) => <span key={c} className="rounded-full border border-border px-4 py-2 text-sm hover:border-primary hover:text-primary">{c}</span>)}</div>
        </section>
        <ComplianceNote />
      </div>
    </div>
  );
}
